import { useEffect, useState } from 'react';
import { Icon } from './ui/Icon';

/**
 * Baner "Dostępna jest nowa wersja" wyświetlany gdy Service Worker wykryje
 * update (nowy SW w stanie `installed` z istniejącym kontrolerem).
 *
 * Zastępuje natywny `confirm()` który był agresywny i blokujący — baner jest
 * dismissible (odłożony do kolejnego reloadu przeglądarki) i non-intrusive.
 *
 * Słucha eventu `sw-update-available` emitowanego przez logikę SW register
 * w `main.tsx`. Klik "Odśwież" wysyła `SKIP_WAITING` i reloaduje stronę.
 */
export function UpdatePrompt() {
  const [update, setUpdate] = useState<ServiceWorker | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<ServiceWorker>).detail;
      if (detail) setUpdate(detail);
    };
    window.addEventListener('sw-update-available', handler);
    return () => window.removeEventListener('sw-update-available', handler);
  }, []);

  if (!update || dismissed) return null;

  const applyUpdate = () => {
    update.postMessage({ type: 'SKIP_WAITING' });
    window.location.reload();
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-4 bottom-4 z-[100] mx-auto max-w-lg rounded-2xl border border-line bg-paper p-4 shadow-xl shadow-slate-900/10 sm:inset-x-auto sm:right-6"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
          <Icon name="arrow-path" className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-ink">
            Dostępna jest nowa wersja
          </p>
          <p className="mt-0.5 text-xs text-muted">
            Odśwież aplikację, aby zaktualizować do najnowszej wersji.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={applyUpdate}
              className="rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-700"
            >
              Odśwież teraz
            </button>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              className="rounded-lg px-3 py-1.5 text-xs font-medium text-ink-soft hover:bg-[var(--paper-2)]"
            >
              Później
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
